import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Category, Product } from '@/types'

import Link from 'next/link'
import { useCallback, useMemo } from 'react'
import { Heart, ShoppingBag, Trash2 } from 'lucide-react'
import { formatIDR } from '@/lib/format'
import { loadCatalog } from '@/lib/catalog'
import { useShop } from '@/components/providers/shop-provider'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { ProductCard } from '@/components/shop/product-card'
import { ProductImage } from '@/components/shop/product-image'
import { WhatsAppLink } from '@/components/shop/whatsapp-link'
import { siteConfig } from '@/constants/site'

interface WishlistPageProps {
    products: Array<Product>
    categories: Array<Category>
}

const WishlistPage: FC<WishlistPageProps> = ({ products, categories }): ReactNode => {
    const { wishlist, hydrated, addToCart, removeFromWishlist } = useShop()
    const byId = useMemo(() => new Map(products.map(product => [product.id, product])), [products])

    const saved = wishlist.map(item => ({ item, product: byId.get(item.productId) ?? null }))
    const available = saved.filter(entry => entry.product && (entry.product.madeToOrder || entry.product.stock > 0))
    const total = saved.reduce((sum, entry) => sum + (entry.product?.price ?? entry.item.price), 0)

    const addAll = () => {
        available.forEach(({ product }) => {
            if (!product) return

            const color = product.colors[0] ?? null

            addToCart({
                productId: product.id,
                slug: product.slug,
                name: product.name,
                price: product.price,
                image: product.image,
                designKey: product.designKey,
                colorLabel: color?.label ?? null,
                colorHex: color?.hex ?? null,
                accentColor: product.accentColor,
                maxQuantity: product.madeToOrder ? null : product.stock
            })
        })
    }

    const shareMessage = useCallback(() => {
        const lines = wishlist.map((item, index) => {
            const product = byId.get(item.productId)

            return `${index + 1}. ${product?.name ?? item.name}, ${formatIDR(product?.price ?? item.price)}`
        })

        return [`My ${siteConfig.name} wishlist:`, '', ...lines, '', 'Handmade crochet dolls, if you are looking for a gift idea!'].join('\n')
    }, [wishlist, byId])

    const fallback = products.filter(product => product.isFeatured).slice(0, 4)

    return (
        <Layout categories={categories}>
            <Seo title='Wishlist' description='The handmade crochet dolls you have saved for later.' />

            <section className='bg-[radial-gradient(120%_120%_at_90%_0%,#fdf1f2_0%,#fffaf3_55%)]'>
                <div className='mx-auto max-w-7xl px-5 pb-12 pt-14 sm:px-8 sm:pt-20'>
                    <p className='text-xs font-bold uppercase tracking-[0.26em] text-blush-600'>
                        Saved for later
                    </p>
                    <h1 className='mt-3 font-heading text-5xl text-cocoa-900 font-soft sm:text-6xl'>
                        Your wishlist
                    </h1>
                    <p className='mt-4 max-w-xl text-base leading-relaxed text-muted-foreground'>
                        Dolls you have fallen for, kept safe on this device. Add them to your cart when you are ready, or share the list with someone planning a gift.
                    </p>
                </div>
            </section>

            <section className='mx-auto max-w-7xl px-5 pb-24 sm:px-8'>
                {!hydrated && (
                    <div className='grid grid-cols-2 gap-6 lg:grid-cols-4' aria-hidden='true'>
                        {[0, 1, 2, 3].map(index => (
                            <div key={index} className='aspect-[4/5] animate-pulse rounded-[28px] bg-cream-100' />
                        ))}
                    </div>
                )}

                {hydrated && saved.length === 0 && (
                    <div className='flex flex-col items-center rounded-[36px] border border-dashed border-blush-300 bg-blush-50/60 px-6 py-16 text-center'>
                        <span className='grid size-16 place-items-center rounded-full bg-white text-blush-500 shadow-sm'>
                            <Heart className='size-7' />
                        </span>
                        <p className='mt-6 font-heading text-3xl text-cocoa-900'>
                            Nothing saved yet
                        </p>
                        <p className='mt-3 max-w-md text-sm leading-relaxed text-muted-foreground'>
                            Tap the heart on any doll to keep it here. It is the easiest way to plan a gift or come back later.
                        </p>
                        <div className='mt-7 flex flex-wrap justify-center gap-3'>
                            <Link href='/shop' className='inline-flex h-12 items-center rounded-full bg-cocoa-800 px-6 text-sm font-semibold text-cream-50 transition hover:bg-cocoa-700'>
                                Browse the collection
                            </Link>
                            <Link href='/' className='inline-flex h-12 items-center rounded-full border border-cocoa-700/15 bg-white px-6 text-sm font-semibold text-cocoa-800 transition hover:bg-cream-100'>
                                Visit the 3D boutique
                            </Link>
                        </div>
                    </div>
                )}

                {hydrated && saved.length > 0 && (
                    <>
                        <div className='mb-10 flex flex-col gap-4 rounded-[28px] bg-cream-100/80 p-5 sm:flex-row sm:items-center sm:justify-between'>
                            <p className='text-sm text-cocoa-700'>
                                <span className='font-bold text-cocoa-900'>{saved.length}</span>
                                {saved.length === 1 ? ' doll saved' : ' dolls saved'}
                                <span aria-hidden='true'> · </span>
                                <span className='font-semibold'>{formatIDR(total)}</span>
                                <span> in total</span>
                            </p>
                            <div className='flex flex-wrap gap-2'>
                                <button
                                    type='button'
                                    onClick={addAll}
                                    disabled={!available.length}
                                    className='inline-flex h-11 items-center gap-2 rounded-full bg-cocoa-800 px-5 text-sm font-semibold text-cream-50 transition hover:bg-cocoa-700 disabled:opacity-50'
                                >
                                    <ShoppingBag className='size-4' />
                                    Add all to cart
                                </button>
                                <WhatsAppLink message={shareMessage} variant='outline' size='sm' className='h-11'>
                                    Share the list
                                </WhatsAppLink>
                            </div>
                        </div>

                        <div className='grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 lg:grid-cols-4'>
                            {saved.map(({ item, product }) => (
                                product
                                    ? <ProductCard key={item.productId} product={product} />
                                    : (
                                        <article key={item.productId} className='flex flex-col'>
                                            <ProductImage
                                                src={item.image}
                                                alt={item.name}
                                                designKey={item.designKey}
                                                mainColor={item.colorHex}
                                                accentColor={item.accentColor}
                                                className='aspect-[4/5] rounded-[28px] opacity-60 grayscale'
                                            />
                                            <p className='mt-4 font-heading text-xl text-cocoa-900'>
                                                <span data-mdlfy-cms={`products:${item.productId}:name`}>
                                                    {item.name}
                                                </span>
                                            </p>
                                            <p className='text-sm text-muted-foreground'>
                                                This doll is no longer in the shop.
                                            </p>
                                            <button
                                                type='button'
                                                onClick={() => removeFromWishlist(item.productId)}
                                                className='mt-3 inline-flex items-center gap-2 self-start text-sm font-semibold text-cocoa-700 hover:text-blush-600'
                                            >
                                                <Trash2 className='size-4' />
                                                Remove
                                            </button>
                                        </article>
                                    )
                            ))}
                        </div>
                    </>
                )}

                {hydrated && saved.length === 0 && fallback.length > 0 && (
                    <div className='mt-20'>
                        <h2 className='font-heading text-3xl text-cocoa-900 font-soft'>
                            A few dolls to start with
                        </h2>
                        <div className='mt-8 grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 lg:grid-cols-4'>
                            {fallback.map(product => (
                                <ProductCard key={product.id} product={product} />
                            ))}
                        </div>
                    </div>
                )}
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<WishlistPageProps> = async () => {
    const { products, categories, ok } = await loadCatalog()

    return { props: { products, categories }, revalidate: ok ? 60 : 10 }
}

export default WishlistPage
